import Add16 from "@carbon/icons-react/es/add/16";
import SettingsAdjust16 from "@carbon/icons-react/es/settings--adjust/16";
import { ColumnProps } from "antd/lib/table";
import { RESPONSIBILITY_ROUTE } from "config/route-consts";
import { Responsibility, ResponsibilityFilter } from "models/Responsibility";
import { Site, SiteFilter } from "models/Site";
import { Status } from "models/Status";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IdFilter } from "react3l-advanced-filters";
import {
  AdvanceEnumFilterMaster,
  Button,
  LayoutCell,
  LayoutHeader,
  OneLineText,
  Pagination,
  StandardTable,
  StatusLine,
  TagFilter,
} from "react3l-ui-library";
import AdvanceIdFilterMaster from "react3l-ui-library/build/components/AdvanceFilterMaster/AdvanceIdFilterMaster";
import InputSearch from "react3l-ui-library/build/components/Input/InputSearch";
import { filterService } from "services/page-services/filter-service";
import { listService } from "services/page-services/list-service";
import { masterService } from "services/page-services/master-service";
import { queryStringService } from "services/page-services/query-string-service";
import {
  getAntOrderType,
  tableService,
} from "services/page-services/table-service";
import nameof from "ts-nameof.macro";
import ResponsibilityAdvanceFilter from "./ResponsibilityAdvanceFilter";
import { responsibilityRepository } from "repositories/responsibility-repository";
import { Edit24 } from "@carbon/icons-react";
import { authorizationService } from "services/common-services/authorization-service";
import { API_RESPONSIBILITY_PREFIX } from "config/api-consts";

/* end individual import */

function ResponsibilityMaster() {
  const [translate] = useTranslation();

  const { validAction } = authorizationService.useAuthorizedAction(
    RESPONSIBILITY_ROUTE.split("/")[RESPONSIBILITY_ROUTE.split("/").length - 1],
    API_RESPONSIBILITY_PREFIX
  );

  const [modelFilter, dispatch] = queryStringService.useQueryString(
    ResponsibilityFilter,
    { skip: 0, take: 10 }
  );
  const [visible, setVisible] = React.useState<boolean>(false);

  const {
    value: filter,
    handleChangeSelectFilter,
    handleChangeAllFilter,
    handleChangeInputSearch,
  } = filterService.useFilter(modelFilter, dispatch);

  const { list, count, loadingList, handleResetList } = listService.useList(
    responsibilityRepository.list,
    responsibilityRepository.count,
    filter,
    dispatch
  );

  const { handleTableChange, handlePagination } = tableService.useTable(
    filter,
    handleChangeAllFilter
  );

  const { handleAction } = listService.useRowSelection(
    responsibilityRepository.delete,
    responsibilityRepository.bulkDelete,
    null,
    null,
    null,
    handleResetList
  );

  const { handleGoCreate, handleGoDetail } = masterService.useMasterAction(
    RESPONSIBILITY_ROUTE,
    handleAction
  );

  const handleCancel = React.useCallback(() => {
    setVisible(false);
  }, []);

  const columns: ColumnProps<Responsibility>[] = useMemo(
    () => [
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "code"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("responsibilities.codeSystem")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        width: 80,
        key: nameof(list[0].id),
        dataIndex: nameof(list[0].id),
        sorter: true,
        sortOrder: getAntOrderType<Responsibility, ResponsibilityFilter>(
          filter,
          nameof(list[0].id)
        ),
        ellipsiss: true,
        render(...params: [string, Responsibility, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]} countCharacters={20} />
            </LayoutCell>
          );
        },
      },

      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "code"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("responsibilities.code")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        width: 200,
        key: nameof(list[0].code),
        dataIndex: nameof(list[0].code),
        sorter: true,
        sortOrder: getAntOrderType<Responsibility, ResponsibilityFilter>(
          filter,
          nameof(list[0].code)
        ),
        ellipsiss: true,
        render(...params: [string, Responsibility, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]} countCharacters={20} />
            </LayoutCell>
          );
        },
      },

      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "name"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("responsibilities.name")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        sorter: true,
        sortOrder: getAntOrderType<Responsibility, ResponsibilityFilter>(
          filter,
          nameof(list[0].name)
        ),
        ellipsiss: true,
        width: 300,
        render(...params: [string, Responsibility, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]} countCharacters={40} />
            </LayoutCell>
          );
        },
      },

      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "site"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("responsibilities.site")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        ellipsiss: true,
        key: nameof(list[0].site),
        dataIndex: nameof(list[0].site),
        sorter: true,
        width: 200,
        sortOrder: getAntOrderType<Responsibility, ResponsibilityFilter>(
          filter,
          nameof(list[0].site)
        ),
        render(site: Site) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={site?.name} countCharacters={20} />
            </LayoutCell>
          );
        },
      },

      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "status"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("responsibilities.status")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        ellipsiss: true,
        key: nameof(list[0].status),
        dataIndex: nameof(list[0].status),
        sorter: true,
        width: 200,
        sortOrder: getAntOrderType<Responsibility, ResponsibilityFilter>(
          filter,
          nameof(list[0].status)
        ),
        render(status: Status) {
          return (
            //fill the render field after generate code;
            <LayoutCell orderType="left" tableSize="md">
              <StatusLine value={status?.name} color={status?.color} />
            </LayoutCell>
          );
        },
      },

      {
        title: (
          <LayoutHeader orderType="left" title={translate("general.action")} />
        ),
        key: "action",
        dataIndex: nameof(list[0].id),
        fixed: "right",
        width: 80,
        align: "center",
        render(id: number, responsibility: Responsibility) {
          return (
            <div className="d-flex button-action-table missions-dxg">
              <div className="d-flex">
                {validAction("update") && (
                  <div className="enable-dxg">
                    <Edit24
                      className="btn-missions-blue"
                      onClick={handleGoDetail(id)}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
    ],
    [filter, handleGoDetail, list, translate, validAction]
  );

  return (
    <>
      <div className="page-content page-content-dxg" id="page-content-dxg-id">
        <div className="info-component ml-4 mb-3">
          <span>{translate("responsibilities.master.header")}</span>
          <span> &gt; </span>
          <span className="info-primary">
            {translate("responsibilities.master.subHeader")}
          </span>
        </div>

        <div className="page page-master m-t--lg m-b--xxs">
          <div className="pt-4 pb-1 ml-3">
            <h5 className="font-bold page-title">
              {translate("responsibilities.master.title")}
            </h5>
          </div>

          <div className="page-master__content">
            <div className="page-master__tag-filter p-t-xxs">
              <TagFilter
                value={filter}
                translate={translate}
                keyTranslate={"responsibilities"}
                handleChangeFilter={handleChangeAllFilter}
                onClear={(value: any) => {
                  return 0;
                }}
              />
            </div>
            <div className="page-master__filter-wrapper d-flex align-items-center justify-content-between px-3">
              <div className="page-master__filter d-flex align-items-center justify-content-start">
                {validAction("filterListSite") && (
                  <div className="">
                    <AdvanceIdFilterMaster
                      value={filter[nameof(list[0].siteId)]["equal"]}
                      placeHolder={translate(
                        "responsibilities.placeholder.site"
                      )}
                      classFilter={SiteFilter}
                      onChange={handleChangeSelectFilter({
                        fieldName: nameof(list[0].site),
                        fieldType: "equal",
                        classFilter: IdFilter,
                      })}
                      getList={responsibilityRepository.filterListSite}
                      label={translate("responsibilities.site")}
                    />
                  </div>
                )}
                {validAction("filterListStatus") && (
                  <div className="">
                    <AdvanceEnumFilterMaster
                      value={filter[nameof(list[0].statusId)]["equal"]}
                      getList={responsibilityRepository.filterListStatus}
                      onChange={handleChangeSelectFilter({
                        fieldName: nameof(list[0].status),
                        fieldType: "equal",
                        classFilter: IdFilter,
                      })}
                      label={translate("responsibilities.status")}
                    />
                  </div>
                )}
              </div>

              <div className="page-master__filter-action-search d-flex align-items-center">
                <Button
                  type="icon-only-ghost"
                  icon={<SettingsAdjust16 />}
                  onClick={() => setVisible(true)}
                  className="btn--xl"
                />
                <InputSearch
                  value={filter.search}
                  classFilter={ResponsibilityFilter}
                  placeHolder={translate("general.placeholder.search")}
                  onChange={handleChangeInputSearch}
                  animationInput={filter?.search ? false : true}
                />
              </div>

              <div className="page-master__actions  d-flex align-items-center justify-content-start">
                <div className="page-master__filter-action d-flex align-items-center">
                  {validAction("create") && (
                    <Button
                      type="primary"
                      className="btn--lg btn-orange-wIcon ml-3"
                      icon={<Add16 />}
                      onClick={handleGoCreate}
                    >
                      {translate("general.actions.create")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="page-master__content-table">
            <StandardTable
              rowKey={nameof(list[0].id)}
              columns={columns}
              dataSource={list}
              isDragable={true}
              tableSize={"md"}
              onChange={handleTableChange}
              spinning={loadingList}
              scroll={{ x: 1000 }}
            />

            <Pagination
              skip={filter?.skip}
              take={filter?.take}
              total={count}
              onChange={handlePagination}
            />
          </div>
        </div>
        {visible && (
          <ResponsibilityAdvanceFilter
            visible={visible}
            handleClose={handleCancel}
            visibleFooter={true}
            loading={false}
            size={"sm"}
            filter={filter}
            setVisible={setVisible}
            handleChangeAllFilter={handleChangeAllFilter}
          />
        )}
      </div>
    </>
  );
}
export default ResponsibilityMaster;
